// contexts/MediaSipContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const MediaSipContext = createContext();

export const useMediaSip = () => {
  const context = useContext(MediaSipContext);
  if (!context) {
    throw new Error('useMediaSip must be used within a MediaSipProvider');
  }
  return context;
};

export const MediaSipProvider = ({ children, sipCaller }) => {
  const [localStream, setLocalStream] = useState(null);
  const [hasAudio, setHasAudio] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);

  // Enhanced setLocalStream that also updates SipCaller
  const setLocalStreamWithSip = useCallback((stream) => {
    console.log('Setting local stream:', stream);
    setLocalStream(stream);
    
    if (sipCaller && typeof sipCaller.setLocalStream === 'function') {
      sipCaller.setLocalStream(stream);
    }
  }, [sipCaller]);

  // Enhanced setHasAudio that updates SipCaller
  const setHasAudioWithSip = useCallback((audio) => {
    console.log('Setting audio capability:', audio);
    setHasAudio(audio);
    
    if (sipCaller && typeof sipCaller.updateMediaCapabilities === 'function') {
      // Use current hasVideo state since we're updating audio
      sipCaller.updateMediaCapabilities(audio, hasVideo);
    }
  }, [sipCaller, hasVideo]);

  // Enhanced setHasVideo that updates SipCaller
  const setHasVideoWithSip = useCallback((video) => {
    console.log('Setting video capability:', video);
    setHasVideo(video);
    
    if (sipCaller && typeof sipCaller.updateMediaCapabilities === 'function') {
      // Use current hasAudio state since we're updating video
      sipCaller.updateMediaCapabilities(hasAudio, video);
    }
  }, [sipCaller, hasAudio]);

  // Initialize SipCaller with current media state when it becomes available
  useEffect(() => {
    if (sipCaller && localStream) {
      console.log('Initializing SipCaller with existing stream');
      if (typeof sipCaller.setLocalStream === 'function') {
        sipCaller.setLocalStream(localStream);
      }
      if (typeof sipCaller.updateMediaCapabilities === 'function') {
        sipCaller.updateMediaCapabilities(hasAudio, hasVideo);
      }
    }
  }, [sipCaller, localStream, hasAudio, hasVideo]);

  // Helper function to get media devices
  const getMediaDevices = useCallback(async (constraints = { audio: true, video: true }) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setLocalStreamWithSip(stream);
      
      // Update capabilities based on what we actually got
      const audioTracks = stream.getAudioTracks();
      const videoTracks = stream.getVideoTracks();
      
      setHasAudioWithSip(audioTracks.length > 0);
      setHasVideoWithSip(videoTracks.length > 0);
      
      return stream;
    } catch (error) {
      console.error('Error accessing media devices:', error);
      throw error;
    }
  }, [setLocalStreamWithSip, setHasAudioWithSip, setHasVideoWithSip]);

  // Helper function to stop all tracks
  const stopLocalStream = useCallback(() => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStreamWithSip(null);
      setHasAudioWithSip(false);
      setHasVideoWithSip(false);
    }
  }, [localStream, setLocalStreamWithSip, setHasAudioWithSip, setHasVideoWithSip]);

  const toggleAudio = useCallback(async (flag) => {
    if (flag) {
      // Enabling audio
      if (!localStream) {
        // No stream exists - create new audio-only stream
        console.log('toggleAudio: No localStream, creating new audio-only stream');
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
          setLocalStreamWithSip(stream);
          const hasAudio = stream.getAudioTracks().length > 0;
          setHasAudioWithSip(hasAudio);
          console.log('toggleAudio: Created new audio-only stream:', hasAudio);
          return; // Stream created with audio, we're done
        } catch (error) {
          console.error('toggleAudio: Error creating stream:', error);
          setHasAudioWithSip(false);
          throw error;
        }
      } else {
        // Stream exists - check if it already has audio
        const existingAudioTracks = localStream.getAudioTracks();
        if (existingAudioTracks.length > 0) {
          // Already has audio - just enable it
          existingAudioTracks.forEach(track => {
            track.enabled = true;
          });
          setHasAudioWithSip(true);
          console.log('toggleAudio: Audio track already exists, enabled it');
          return;
        }
        
        // Stream exists but no audio - add audio track
        try {
          console.log('toggleAudio: Adding audio track to existing stream');
          const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
          const newAudioTrack = audioStream.getAudioTracks()[0];
          
          if (newAudioTrack && localStream && localStream.active) {
            localStream.addTrack(newAudioTrack);
            setHasAudioWithSip(true);
            console.log('toggleAudio: Audio track added successfully');
          } else {
            console.warn('toggleAudio: No audio track received or stream inactive');
            if (newAudioTrack) newAudioTrack.stop();
            setHasAudioWithSip(false);
          }
        } catch (error) {
          console.error('toggleAudio: Error adding audio track:', error);
          setHasAudioWithSip(false);
          throw error;
        }
      }
    } else {
      // Disabling audio
      if (!localStream) {
        console.warn('toggleAudio: No localStream available to disable audio');
        setHasAudioWithSip(false);
        return;
      }
      
      // Disable audio tracks (but don't stop them - keep stream alive)
      const audioTracks = localStream.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = false;
      });
      setHasAudioWithSip(false);
      console.log('toggleAudio: Audio tracks disabled');
    }
  }, [localStream, setHasAudioWithSip, setLocalStreamWithSip]);

  const toggleVideo = useCallback(async (flag) => {
    if (flag) {
      // Enabling video
      if (!localStream) {
        // No stream exists - create new stream with both audio and video
        console.log('toggleVideo: No localStream, creating new stream with audio and video');
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
          setLocalStreamWithSip(stream);
          const hasVideo = stream.getVideoTracks().length > 0;
          const hasAudio = stream.getAudioTracks().length > 0;
          setHasVideoWithSip(hasVideo);
          setHasAudioWithSip(hasAudio);
          console.log('toggleVideo: Created new stream with video:', hasVideo, 'audio:', hasAudio);
          return; // Stream created with video, we're done
        } catch (error) {
          console.error('toggleVideo: Error creating stream:', error);
          setHasVideoWithSip(false);
          throw error;
        }
      } else {
        // Stream exists - check if it already has video
        const existingVideoTracks = localStream.getVideoTracks();
        if (existingVideoTracks.length > 0 && existingVideoTracks.some(track => track.readyState === 'live')) {
          // Already has video - just enable it
          existingVideoTracks.forEach(track => {
            track.enabled = true;
          });
          setHasVideoWithSip(true);
          console.log('toggleVideo: Video track already exists, enabled it');
          return;
        }
        
        // Stream exists but no video - add video track
        try {
          console.log('toggleVideo: Adding video track to existing stream');
          const videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
          const newVideoTrack = videoStream.getVideoTracks()[0];
          
          if (newVideoTrack && localStream && localStream.active) {
            localStream.addTrack(newVideoTrack);
            setHasVideoWithSip(true);
            console.log('toggleVideo: Video track added successfully');
            
            // Store video stream for cleanup
            if (sipCaller && sipCaller._videoStreamsByTrackId) {
              sipCaller._videoStreamsByTrackId.set(newVideoTrack.id, videoStream);
            }
          } else {
            console.warn('toggleVideo: No video track received or stream inactive');
            if (newVideoTrack) newVideoTrack.stop();
            setHasVideoWithSip(false);
          }
        } catch (error) {
          console.error('toggleVideo: Error adding video track:', error);
          setHasVideoWithSip(false);
          throw error;
        }
      }
    } else {
      // Disabling video
      if (!localStream) {
        console.warn('toggleVideo: No localStream available to disable video');
        setHasVideoWithSip(false);
        return;
      }
      // Disabling video: Stop ALL video tracks completely to turn off camera
      console.log('Disabling video - collecting all video tracks from all sources');
        const allVideoTracks = new Set();
        
      // 1. Collect from localStream
      localStream.getVideoTracks().forEach(track => {
          allVideoTracks.add(track);
        console.log('Found video track in localStream:', track.id, track.label);
        });
        
      // 2. Collect from MediaPermission stream if available (CRITICAL for first-time permission)
      // Only check if it's a different stream object (not the same as localStream)
      if (window.__mediaPermissionStreamRef__ && window.__mediaPermissionStreamRef__.current) {
        const permissionStream = window.__mediaPermissionStreamRef__.current;
        // Only process if it's a different stream object
        if (permissionStream && permissionStream !== localStream && typeof permissionStream.getVideoTracks === 'function') {
          const permissionVideoTracks = Array.from(permissionStream.getVideoTracks());
          console.log(`Found ${permissionVideoTracks.length} video track(s) in MediaPermission stream (different from localStream)`);
          
          permissionVideoTracks.forEach(track => {
            allVideoTracks.add(track);
            console.log('Found video track in MediaPermission stream:', track.id, track.label, 'readyState:', track.readyState);
          });
        } else if (permissionStream === localStream) {
          console.log('MediaPermission stream is the same as localStream, already collected');
        }
      }
      
      // 3. Collect from sipCaller if available
      if (sipCaller && sipCaller.localStream) {
        sipCaller.localStream.getVideoTracks().forEach(track => {
          allVideoTracks.add(track);
          console.log('Found video track in sipCaller.localStream:', track.id);
        });
      }
      
      // 4. Collect from sipCaller's _videoStreamsByTrackId
        if (sipCaller && sipCaller._videoStreamsByTrackId) {
          sipCaller._videoStreamsByTrackId.forEach((videoStream, trackId) => {
            if (videoStream && typeof videoStream.getVideoTracks === 'function') {
              videoStream.getVideoTracks().forEach(track => {
                allVideoTracks.add(track);
              console.log('Found video track in stored stream:', track.id);
              });
            }
          });
        }
        
      // 5. Collect from ALL video elements on the page
      document.querySelectorAll('video').forEach(videoEl => {
        if (videoEl && videoEl.srcObject) {
          const videoStream = videoEl.srcObject;
          if (videoStream && typeof videoStream.getVideoTracks === 'function') {
            videoStream.getVideoTracks().forEach(track => {
              allVideoTracks.add(track);
              console.log('Found video track in video element:', track.id);
            });
          }
        }
      });
      
      console.log(`Total video tracks found: ${allVideoTracks.size}`);
      
      // STEP 1: Remove tracks from streams FIRST (before stopping)
      // Remove from localStream
          const videoTracksToRemove = Array.from(localStream.getVideoTracks());
          videoTracksToRemove.forEach(track => {
            try {
              console.log('Removing video track from localStream:', track.id);
              localStream.removeTrack(track);
        } catch (e) {
          console.error('Error removing track from localStream:', e);
            }
          });
          
      // Remove from MediaPermission stream (CRITICAL - this is the stream from first permission)
      // Only process if it's a different stream object
      if (window.__mediaPermissionStreamRef__ && window.__mediaPermissionStreamRef__.current) {
        const permissionStream = window.__mediaPermissionStreamRef__.current;
        if (permissionStream && permissionStream !== localStream && typeof permissionStream.getVideoTracks === 'function') {
          const permissionTracks = Array.from(permissionStream.getVideoTracks());
          console.log(`Removing ${permissionTracks.length} video track(s) from MediaPermission stream`);
          permissionTracks.forEach(track => {
                try {
              console.log('Removing video track from MediaPermission stream:', track.id);
              permissionStream.removeTrack(track);
            } catch (e) {
              console.error('Error removing track from MediaPermission stream:', e);
              }
            });
          }
        }
        
      // Remove from sipCaller.localStream
      if (sipCaller && sipCaller.localStream) {
        const sipTracks = Array.from(sipCaller.localStream.getVideoTracks());
        sipTracks.forEach(track => {
            try {
              console.log('Removing video track from sipCaller.localStream:', track.id);
              sipCaller.localStream.removeTrack(track);
          } catch (e) {
            console.error('Error removing track from sipCaller.localStream:', e);
          }
        });
      }
      
      // STEP 2: Stop ALL video tracks (this actually turns off the camera hardware)
        console.log(`Stopping ${allVideoTracks.size} video track(s)`);
        allVideoTracks.forEach(track => {
          try {
          if (track) {
            console.log('Stopping video track:', track.id, track.label, 'readyState:', track.readyState);
            // Force stop regardless of state
            if (track.readyState !== 'ended') {
              track.stop(); // This is what actually turns off the camera
            }
            // Double-check: if still not ended, try again
            if (track.readyState !== 'ended') {
              console.warn('Track still not ended after stop(), trying again:', track.id);
              try {
                track.stop();
              } catch (e2) {
                console.error('Error on second stop attempt:', e2);
              }
            }
            console.log('Track stopped, final readyState:', track.readyState);
          }
        } catch (e) {
          console.error('Error stopping video track:', e);
          }
        });
        
      // STEP 2b: Force stop any remaining video tracks using MediaDevices API
      // This is a last resort to ensure camera is released
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        console.log('Available media devices:', devices.length);
      } catch (e) {
        console.error('Error enumerating devices:', e);
      }
      
      // STEP 3: Clear ALL video elements completely
      document.querySelectorAll('video').forEach(videoEl => {
        if (videoEl) {
          console.log('Clearing video element:', videoEl.id || 'unnamed');
          videoEl.pause();
          if (videoEl.srcObject) {
            // Stop tracks from the element's stream too
            if (videoEl.srcObject.getVideoTracks) {
              videoEl.srcObject.getVideoTracks().forEach(track => {
                try {
                    track.stop();
                } catch (e) {
                  console.error('Error stopping track from video element:', e);
                }
              });
            }
            videoEl.srcObject = null;
          }
          videoEl.load();
        }
      });
        
      // STEP 4: Clear sipCaller's video streams map
      if (sipCaller && sipCaller._videoStreamsByTrackId) {
        sipCaller._videoStreamsByTrackId.forEach((videoStream) => {
          if (videoStream && typeof videoStream.getVideoTracks === 'function') {
            videoStream.getVideoTracks().forEach(track => {
              try {
                track.stop();
              } catch (e) {
                console.error('Error stopping track from stored stream:', e);
          }
            });
          }
        });
        sipCaller._videoStreamsByTrackId.clear();
      }
      
      // STEP 5: Update state
        setHasVideoWithSip(false);
      console.log('Video disabled: All tracks stopped, streams cleared, camera should be off');
    }
  }, [localStream, setHasVideoWithSip, sipCaller, setLocalStreamWithSip, setHasAudioWithSip]);

  const contextValue = {
    // Media state
    localStream,
    hasAudio,
    hasVideo,
    
    // Enhanced setters that sync with SipCaller
    setLocalStream: setLocalStreamWithSip,
    setHasAudio: setHasAudioWithSip,
    setHasVideo: setHasVideoWithSip,
    
    // Helper functions
    getMediaDevices,
    stopLocalStream,
    toggleAudio,
    toggleVideo,
    
    // SipCaller instance
    sipCaller
  };

  return (
    <MediaSipContext.Provider value={contextValue}>
      {children}
    </MediaSipContext.Provider>
  );
};

// Legacy hooks for backward compatibility
export const useLocalMedia = () => {
  const context = useMediaSip();
  return {
    localStream: context.localStream,
    hasAudio: context.hasAudio,
    hasVideo: context.hasVideo,
    setLocalStream: context.setLocalStream,
    setHasAudio: context.setHasAudio,
    setHasVideo: context.setHasVideo,
    getMediaDevices: context.getMediaDevices,
    stopLocalStream: context.stopLocalStream,
    toggleAudio: context.toggleAudio,
    toggleVideo: context.toggleVideo
  };
};

export const useSipCaller = () => {
  const context = useMediaSip();
  return context.sipCaller;
};

// HOC for backward compatibility with your existing withSipCallerContext
export const withMediaSipContext = (Component) => {
  return (props) => (
    <MediaSipContext.Consumer>
      {(context) => (
        <Component 
          {...props} 
          sipCaller={context.sipCaller}
          localMedia={{
            localStream: context.localStream,
            hasAudio: context.hasAudio,
            hasVideo: context.hasVideo,
            setLocalStream: context.setLocalStream,
            setHasAudio: context.setHasAudio,
            setHasVideo: context.setHasVideo
          }}
        />
      )}
    </MediaSipContext.Consumer>
  );
};